import React from 'react';

function findScrollParent(el: HTMLElement): HTMLElement | Window {
  let node = el.parentElement;
  while (node) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
    node = node.parentElement;
  }
  return window;
}

/**
 * Whether the area `ref` scrolls within has moved down past `threshold` px.
 *
 * It turns on past `threshold` but only off again near the very top, so
 * content that changes height as a result — a toolbar folding its filters
 * away — cannot flip it back and forth at the boundary.
 *
 * `resetKey`: change it when the page's scrolling area may have changed (a
 * side panel opening can move the scrollbar to another element).
 */
export function useScrolledPast(
  ref: React.RefObject<HTMLElement | null>,
  threshold: number = 64,
  resetKey?: unknown,
): boolean {
  const [past, setPast] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const scroller = findScrollParent(el);
    const read = () => {
      const top = scroller === window ? window.scrollY : (scroller as HTMLElement).scrollTop;
      setPast((prev) => (prev ? top > 8 : top > threshold));
    };
    read();
    scroller.addEventListener('scroll', read, { passive: true });
    return () => scroller.removeEventListener('scroll', read);
  }, [ref, threshold, resetKey]);

  return past;
}

/**
 * A section that folds itself away once the page is scrolled down — a
 * toolbar's filters, say — and comes back at the top.
 *
 * The reader's own choice still counts: at the top, `toggle` opens and closes
 * it for good; scrolled down, `toggle` shows it for a look, and it folds
 * again the next time the page leaves the top.
 */
export function useCollapseOnScroll(
  ref: React.RefObject<HTMLElement | null>,
  { threshold = 64, resetKey }: { threshold?: number; resetKey?: unknown } = {},
): { shown: boolean; toggle: () => void; scrolled: boolean } {
  const scrolled = useScrolledPast(ref, threshold, resetKey);
  const [open, setOpen] = React.useState(true);
  const [peek, setPeek] = React.useState(false);

  React.useEffect(() => {
    if (scrolled) setPeek(false);
  }, [scrolled]);

  const toggle = React.useCallback(() => {
    if (scrolled) setPeek((p) => !p);
    else setOpen((o) => !o);
  }, [scrolled]);

  return { shown: scrolled ? peek : open, toggle, scrolled };
}
